"use client"

import { motion, AnimatePresence } from "framer-motion"
import { Clock, Trash2, TrendingUp, Brain } from "lucide-react"
import { useState } from "react"

export default function ActivityHistory({ activities, onSelectActivity, onDeleteActivity }) {
  const [filter, setFilter] = useState("all")

  const getActivityIcon = (activity) => {
    const activityLower = activity.toLowerCase()
    if (activityLower.includes("read")) return "📖"
    if (activityLower.includes("speak") || activityLower.includes("talk")) return "🗣️"
    if (activityLower.includes("listen") || activityLower.includes("music")) return "🎵"
    if (activityLower.includes("write")) return "✍️"
    if (activityLower.includes("learn")) return "🎓"
    if (activityLower.includes("exercise") || activityLower.includes("run")) return "🏃"
    if (activityLower.includes("meditate") || activityLower.includes("relax")) return "🧘"
    if (activityLower.includes("mood") || activityLower.includes("emotion")) return "😊"
    return "🧠"
  }

  const getTimeAgo = (timestamp) => {
    const now = new Date()
    const activityTime = new Date(timestamp)
    const diffMs = now - activityTime
    const diffMins = Math.floor(diffMs / 60000)
    
    if (diffMins < 1) return "Just now"
    if (diffMins < 60) return `${diffMins}m ago`
    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours}h ago`
    const diffDays = Math.floor(diffHours / 24)
    return `${diffDays}d ago`
  }

  return (
    <div className="activity-history-container">
      <motion.div
        className="history-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h2>
          <Clock size={24} />
          Activity History
        </h2>
        <div className="history-filters">
          <button 
            className={`filter-btn ${filter === "all" ? "active" : ""}`}
            onClick={() => setFilter("all")}
          >
            All
          </button>
          <button 
            className={`filter-btn ${filter === "recent" ? "active" : ""}`}
            onClick={() => setFilter("recent")}
          >
            Recent
          </button>
          <button 
            className={`filter-btn ${filter === "favorites" ? "active" : ""}`}
            onClick={() => setFilter("favorites")}
          >
            Favorites
          </button>
        </div>
      </motion.div>

      <div className="history-list">
        <AnimatePresence>
          {activities && activities.length > 0 ? (
            activities.map((activity, index) => (
              <motion.div
                key={activity.id || index}
                className="history-item glass-card"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.02, x: 5 }}
                onClick={() => onSelectActivity && onSelectActivity(activity)}
              >
                <div className="history-item-icon">
                  {getActivityIcon(activity.name || activity)}
                </div>
                <div className="history-item-content">
                  <h4>{activity.name || activity}</h4>
                  <div className="history-item-meta">
                    <span className="history-time">
                      <Clock size={14} />
                      {getTimeAgo(activity.timestamp || new Date())}
                    </span>
                    {activity.regions && (
                      <span className="history-regions">
                        <Brain size={14} />
                        {activity.regions.length} regions
                      </span>
                    )}
                  </div>
                </div>
                <div className="history-item-actions">
                  <motion.button
                    className="action-btn view"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => {
                      e.stopPropagation()
                      onSelectActivity && onSelectActivity(activity)
                    }}
                  >
                    <TrendingUp size={18} />
                  </motion.button>
                  <motion.button
                    className="action-btn delete"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => {
                      e.stopPropagation()
                      onDeleteActivity && onDeleteActivity(activity.id || index)
                    }}
                  >
                    <Trash2 size={18} />
                  </motion.button>
                </div>
              </motion.div>
            ))
          ) : (
            <motion.div
              className="empty-history"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Brain size={48} />
              <h3>No activities yet</h3>
              <p>Start analyzing brain activities to build your history</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {activities && activities.length > 0 && (
        <motion.div
          className="history-stats"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="stat-item">
            <span className="stat-label">Total Activities</span>
            <span className="stat-value">{activities.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">This Week</span>
            <span className="stat-value">{Math.min(activities.length, 12)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Avg. per Day</span>
            <span className="stat-value">{(activities.length / 7).toFixed(1)}</span>
          </div>
        </motion.div>
      )}
    </div>
  )
}
